import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, StatusPill } from "@/components/ui-kit";
import { formatINR, formatNum, formatDateTime } from "@/lib/format";
import {
  Building2, CheckCircle2, XCircle, Car, Users, TrendingUp, AlertOctagon,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="admin-dashboard-page">
      <PageHeader title="Platform Overview" subtitle="System-wide health, tenants, and revenue." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Owners" value={formatNum(data.total_owners)} icon={Building2} testid="stat-total-owners" />
        <StatCard label="Active" value={formatNum(data.active_owners)} icon={CheckCircle2} testid="stat-active-owners" />
        <StatCard label="Suspended" value={formatNum(data.suspended_owners)} icon={XCircle} testid="stat-suspended" />
        <StatCard label="Expired" value={formatNum(data.expired_subscriptions)} icon={AlertOctagon} testid="stat-expired" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Revenue" value={formatINR(data.revenue)} accent icon={TrendingUp} testid="stat-revenue" />
        <StatCard label="Vehicles" value={formatNum(data.total_vehicles)} icon={Car} testid="stat-vehicles" />
        <StatCard label="Drivers" value={formatNum(data.total_drivers)} icon={Users} testid="stat-drivers" />
        <StatCard label="Bookings" value={formatNum(data.total_bookings)} testid="stat-bookings" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Monthly Revenue</div>
              <div className="font-display font-bold text-xl text-asphalt mt-1">Last 6 months</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.monthly_revenue}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFB800" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FFB800" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
              <YAxis stroke="#71717a" fontSize={12} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#FFB800" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Package Sales</div>
          <div className="font-display font-bold text-xl text-asphalt mt-1 mb-4">By plan</div>
          {data.package_sales.length === 0 ? (
            <div className="text-sm text-zinc-500">No subscriptions yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.package_sales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#09090B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-200">
            <div className="font-display font-bold text-asphalt">Recent Tenants</div>
          </div>
          {data.recent_tenants.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No tenants yet.</div>
          ) : (
            <table className="tbl">
              <thead><tr><th>Company</th><th>Plan</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {data.recent_tenants.map((t) => (
                  <tr key={t.id}>
                    <td className="font-medium text-asphalt">{t.company_name}</td>
                    <td>{t.plan_name || "—"}</td>
                    <td><StatusPill status={t.status} /></td>
                    <td className="text-zinc-500">{formatDateTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-200">
            <div className="font-display font-bold text-asphalt">Recent Payments</div>
          </div>
          {data.recent_payments.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No payments yet.</div>
          ) : (
            <table className="tbl">
              <thead><tr><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {data.recent_payments.map((p) => (
                  <tr key={p.id}>
                    <td className="font-medium text-asphalt">{p.plan_name}</td>
                    <td className="font-mono">{formatINR(p.amount)}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-zinc-500">{formatDateTime(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
